import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col, Spin, message, Input, Popconfirm } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AuthenticatedPopupLayout from "../../layouts/authenticated-popup";
import FlexCard from "../../components/flex-card";
import InputCnpj from "../../components/input-cnpj";
import EmpresaUnidadesTable from "./_unidadesTable";
import {
  assistenteSelector,
  fetchAssistente,
} from "../../store/empresa";
import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import { useInformacoes } from "../../hooks/useInformacoes";
import SelectEmpresaTipo from "../../components/select-empresa-tipo";
import SelectAcesso from "../../components/select-acesso";
import SelectBoolean from "../../components/select-boolean";
import InputNumber from "../../components/input-number";
import InputDate from "../../components/input-date";
import axios from "axios";
import SelectMonth from "../../components/select-month";
import { openPopupEmpresa } from "../../util/window";
import SociosTable from './_sociosTable';


export default function CriarEmpresaPage() {
  const { t } = useTranslation();
  const [formEmpresa] = Form.useForm();
  const [unidadesData, setUnidadesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const dispatch = useDispatchWithResult();
  const cnpj = Form.useWatch("cnpj", formEmpresa);
  const informacoes = useInformacoes([formEmpresa]);
  const [loadingMessage, setLoadingMessage] = useState("");
  const arrayAssistente = useSelector(assistenteSelector);

const fetchEmpresa = async (cnpjNumber) => {
  setLoading(true);
  setLoadingMessage("Carregando dados...");
  try {
    await new Promise((r) => setTimeout(r, 400));
    setLoadingMessage("Carregando dados da empresa...");
    await dispatch(fetchAssistente({ cnpj: cnpjNumber }));
    setLoadingMessage("Carregando dados das unidades...");
    await new Promise((r) => setTimeout(r, 400));
    setLoadingMessage("Quase pronto...");
  } finally {
    setTimeout(() => {
      setLoading(false);
      setLoadingMessage("");
    }, 600);
  }
};


  useEffect(() => {
    if (!cnpj) return;
    const onlyNumbers = cnpj.replace(/\D/g, "");
    if (onlyNumbers.length === 14) {
      fetchEmpresa(onlyNumbers);
    }
  }, [cnpj]);

  useEffect(() => {
    if (!arrayAssistente.empresa || Object.keys(arrayAssistente.empresa).length === 0)
      return;

    const empresaDados = arrayAssistente.empresa.empresa || {};
    const empresaPrincipal = arrayAssistente.empresa;
    const municipio = empresaPrincipal.municipio || {};
    const socios = arrayAssistente.socios

    formEmpresa.setFieldsValue({
      cnpj: empresaPrincipal.cnpj,
      razaoSocial: empresaDados.razaoSocial,
      tipo: null,
      capitalSocialSubscrito: empresaDados.capitalSocial || 0,
      capitalSocialIntegralizado: 0,
      quotas: 0,
      eppme: empresaDados.porte === "03" || empresaDados.porte === "01",
      def: false,
      tributario: true,
      dataLimite: null,
      mesEncerramento: null,
      obs: "",
      sisbacenLogin: null,
      sisbacenSenha: null,
    });

    const unidadesAssistente =
      arrayAssistente.unidades?.map((u, index) => {
        const descricoesCnaes = [
          u.cnaePrincipal?.descricao,
          ...(u.cnaesSecundaria?.map((c) => c.descricao) || []),
        ].filter(Boolean);

        return {
          key: index + 1,
          cnpj: u.cnpj,
          apelido: `${u.bairro} - ${u.municipio?.nome} - ${u.uf}`,
          tipo: "Filial",
          cnaePrimario: u.cnaePrincipal?.codigo || 0,
          uf: u.uf,
          cidade: u.municipio?.nome,
          cep: u.cep,
          endereco: `${u.logradouro || ""}, ${u.numero || ""} ${u.complemento || ""} - ${u.bairro || ""}`.trim(),
          cnaesSecundarios: u.cnaesSecundaria?.map((c) => c.codigo).join(", ") || "",
          objetoSocial: descricoesCnaes.join(", "),
          obs: "",
          ativa: true,
        };
      }) || [];

    const descricoesCnaesMatriz = [
      empresaPrincipal.cnaePrincipal?.descricao,
      ...(empresaPrincipal.cnaesSecundaria?.map((c) => c.descricao) || []),
    ].filter(Boolean);

    const matriz = {
      key: 0,
      cnpj: empresaPrincipal.cnpj,
      apelido: `${empresaPrincipal.bairro} - ${municipio.nome} - ${empresaPrincipal.uf}`,
      tipo: "Matriz",
      cnaePrimario: empresaPrincipal.cnaePrincipal?.codigo || 0,
      uf: empresaPrincipal.uf,
      cidade: municipio.nome,
      cep: empresaPrincipal.cep,
      endereco: `${empresaPrincipal.logradouro || ""}, ${empresaPrincipal.numero || ""} ${empresaPrincipal.complemento || ""} - ${empresaPrincipal.bairro || ""}`.trim(),
      cnaesSecundarios: empresaPrincipal.cnaesSecundaria?.map((c) => c.codigo).join(", ") || "",
      objetoSocial: descricoesCnaesMatriz.join(", "),
      obs: "",
      ativa: true,
    };

    const unidades = [matriz, ...unidadesAssistente];
    setUnidadesData(unidades);
  }, [arrayAssistente, formEmpresa]);

const handleSubmit = () => {
  formEmpresa.validateFields().then((empresa) => {
    const sociosPreparados = (arrayAssistente.socios || []).map(socio => ({
      documento: socio.documento,
      nome: socio.nome,
      email: socio.email ?? '',
      exterior: socio.exterior ?? false,
      incapacitado: socio.incapacitado ?? false,
      obs: socio.obs ?? ''
    }));

    const dadosCompletos = { 
      ...empresa, 
      unidades: unidadesData, 
      socios: sociosPreparados, 
    };

    setLoading(true);

    axios
      .post("empresas/Assistente", dadosCompletos)
      .then((response) => {
        message.success("Empresa, unidades e sócios incluídos");
        setLoading(false);
        formEmpresa.resetFields();
        setUnidadesData([]);
        window.close();
        openPopupEmpresa({ id: response.data.id }, "cadastro", { prefix: "/societario" });
      })
      .catch((err) => {
        const erros = err.erros;

        if (erros && typeof erros === "object") {
          const mensagens = Object.values(erros).flat();
          mensagens.forEach((msg) => message.error(msg));
        } else {
          message.error("Ocorreu um erro inesperado");
        }
        setLoading(false);
      });
  });
};


  return (
    <AuthenticatedPopupLayout>
      <FlexCard style={styles.flexCard}>
        <Spin spinning={loading} tip={loadingMessage || "Carregando..."}> 
          <div style={{ marginBottom: 25 }}>
            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Assistente de Empresas</h1>
            <h2 style={{ fontSize: 14, fontWeight: "normal", color: "#888" }}>
              Preencha o CNPJ para listar dados da empresa, unidades e sócios.
            </h2>
          </div>

          <Form form={formEmpresa} layout="vertical" initialValues={{}}>
            <Row gutter={[16, 8]}>
              <Col flex="200px">
                <Form.Item
                  name="cnpj"
                  label={t("CNPJ")}
                  rules={[{ required: true, message: t("Informe o CNPJ") }]}
                >
                  <InputCnpj />
                </Form.Item>
              </Col>
              <Col flex="500px">
                <Form.Item
                  name="razaoSocial"
                  label={t("Razão Social")}
                  rules={[{ required: true, message: t("Informe a razão social") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 8]}>
              <Col flex="200px">
                <Form.Item
                  name="acessoId"
                  label={t("Acesso")}
                  rules={[{ required: true, message: t("Selecione um acesso") }]}
                >
                  <SelectAcesso />
                </Form.Item>
              </Col>
              <Col flex="250px">
                <Form.Item
                  name="tipo"
                  label={t("Tipo Corporativo")}
                  rules={[{ required: true, message: t("Selecione o tipo da empresa") }]}
                >
                  <SelectEmpresaTipo />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 8]}>
              <Col flex="200px">
                <Form.Item name="quotas" label={t("Número de Quotas/Ações")}>
                  <InputNumber />
                </Form.Item>
              </Col>
              <Col flex="250px">
                <Form.Item name="capitalSocialSubscrito" label={t("Capital Social Subscrito (R$)")}>
                  <InputNumber maximumFractionDigits={2} />
                </Form.Item>
              </Col>
              <Col flex="250px">
                <Form.Item name="capitalSocialIntegralizado" label={t("Capital Social Integralizado (R$)")}>
                  <InputNumber maximumFractionDigits={2} />
                </Form.Item>
              </Col>
              <Col flex="200px">
                <Form.Item name="dataLimite" label={t("Data Limite p/ Integralização")}>
                  <InputDate />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 8]}>
              <Col flex="200px">
                <Form.Item name="mesEncerramento" label={t("Término do Exercício Social")}>
                  <SelectMonth />
                </Form.Item>
              </Col>
              <Col flex="150px">
                <Form.Item name="eppme" label={t("EPP ou ME")}>
                  <SelectBoolean />
                </Form.Item>
              </Col>
              <Col flex="100px" />
              <Col flex="150px">
                <Form.Item name="tributario" label={t("Dados do Contador")}>
                  <SelectBoolean />
                </Form.Item>
              </Col>
              <Col flex="150px">
                <Form.Item name="def" label={t("Declarações Bacen")}>
                  <SelectBoolean />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 8]}>
              <Col flex="800px">
                <Form.Item name="obs" label={t("Observação")}>
                  <Input.TextArea autoSize />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 8]}>
              <Col flex="200px">
                <Form.Item name="sisbacenLogin" label={t("Login Sisbacen")}>
                  <Input autoComplete="off"/>
                </Form.Item>
              </Col>
              <Col flex="200px">
                <Form.Item name="sisbacenSenha" label={t("Senha Sisbacen")}>
                  <Input.Password autoComplete="new-password"/>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <div style={styles.sectionHeader}>
            <h3>Dados das Unidades</h3>
          </div>

          <EmpresaUnidadesTable
            dataSource={unidadesData}
            loading={loading}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
          />

          <div style={styles.sectionHeader}>
          <h3>Dados dos Sócios</h3>
          </div>

          <SociosTable dataSource={arrayAssistente.socios || []} loading={loading} />
        </Spin>

        <div style={styles.confirmButtonContainer}>
          <Popconfirm
            title="Confirmar criação ?"
            description="Tem certeza que deseja criar esta empresa e suas unidades?"
            okText="Sim"
            cancelText="Cancelar"
            onConfirm={handleSubmit}
          >
            <Button type="primary">
              {t("Salvar empresa")}
            </Button>
          </Popconfirm>
        </div>
      </FlexCard>
    </AuthenticatedPopupLayout>
  );
}

const styles = {
  flexCard: { position: "relative", paddingBottom: 80 },
  confirmButtonContainer: { position: "absolute", bottom: 20, right: 20 },
  sectionHeader: {
    borderBottom: "1px solid #ddd",
    margin: "30px 0 10px 0",
    paddingBottom: 4,
  },
};
